import { Link } from '@tanstack/react-router';
import {
  type NicheConfig,
  type NicheSlug,
  NICHE_MOCKUP_ROWS,
  BRAND,
} from '@maintenance-tracker/shared';
import {
  SeoHead,
  breadcrumbJsonLd,
  faqJsonLd,
  organizationJsonLd,
} from '@/components/marketing/seo-head';
import { signupSearchForNiche } from '@/lib/niche-signup';
import {
  MarketingHeader,
  ToolMarquee,
  ProblemSection,
  HowItWorksSection,
  FeatureTabs,
  featureTabs,
  FeatureGrid,
  MetricsBand,
  PricingSection,
  BillingNoticeSection,
  FaqSection,
  FinalCta,
  FooterSection,
  MobileCta,
  Cta,
  Eyebrow,
  SectionHeading,
  FloatChip,
  MockDashboard,
  MockCampaign,
} from '@/pages/landing';
import {
  ShieldCheck,
  TrendingDown,
  TrendingUp,
  PhoneCall,
  Send,
  RefreshCw,
  Sparkles,
  Plug,
  LineChart,
  ArrowRight,
  FileText,
  AlertTriangle,
  DollarSign,
  Star,
  Bell,
} from 'lucide-react';

const FEATURE_ICONS = [ShieldCheck, LineChart, PhoneCall, Send, RefreshCw, TrendingUp, Sparkles, Plug];

type NicheLandingPageProps = {
  config: NicheConfig;
};

export function NicheLandingPage({ config }: NicheLandingPageProps) {
  const signupSearch = signupSearchForNiche(config.slug);
  const { agreementCount, avgPlanPrice, lapseRatePercent, pullThroughNote } = config.revenueExample;
  const agreementRevenue = Math.round(agreementCount * avgPlanPrice * (lapseRatePercent / 100));

  const jsonLd = [
    organizationJsonLd(),
    breadcrumbJsonLd([
      { name: 'Home', path: '/' },
      { name: config.trade, path: `/for/${config.slug}` },
    ]),
    faqJsonLd(config.faqs),
    {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: `${BRAND.name} for ${config.trade}`,
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD', description: '7-day free trial' },
      description: config.seo.description,
    },
  ];

  const nicheFeatures = config.features.map((f, i) => ({
    icon: FEATURE_ICONS[i % FEATURE_ICONS.length],
    title: f.title,
    desc: f.desc,
  }));

  const nicheTabs = featureTabs.map((t) =>
    t.tab === 'Automations' ? { ...t, Mock: () => <MockCampaign trade={config.trade} /> } : t,
  );

  const nicheMetrics = [
    {
      value: `$${Math.round(agreementRevenue / 1000)}k`,
      label: `in agreement revenue at risk each year for a ${agreementCount}-agreement ${config.trade} shop`,
    },
    { value: `${lapseRatePercent}%`, label: 'of members lapse without an automated renewal system' },
    { value: '3 min', label: 'from spreadsheet upload to your first at-risk list' },
  ];

  const metricsBody = (
    <>
      Illustrative math for a <span className="font-semibold text-ink-fg">{agreementCount}-agreement</span>{' '}
      {config.audience} shop: average plan <span className="font-semibold text-ink-fg">${avgPlanPrice}/yr</span>,
      expected lapse <span className="font-semibold text-ink-fg">{lapseRatePercent}%</span> without automation is{' '}
      <span className="font-semibold text-ink-fg">~${agreementRevenue.toLocaleString()}/yr</span> in agreement revenue
      at risk. {pullThroughNote}.
    </>
  );

  return (
    <div className="min-h-screen overflow-x-hidden bg-canvas font-sans text-canvas-fg antialiased">
      <SeoHead
        title={config.seo.title}
        description={config.seo.description}
        canonicalPath={`/for/${config.slug}`}
        jsonLd={jsonLd}
      />

      <MarketingHeader signupSearch={signupSearch} />
      <NicheHero config={config} signupSearch={signupSearch} agreementRevenue={agreementRevenue} />
      <ToolMarquee tools={config.fsmTools} />

      <ProblemSection
        eyebrow="The leak"
        title={
          <>
            Your next $50k isn&apos;t in new leads —
            <br className="hidden md:block" /> it&apos;s leaking out the back
          </>
        }
        desc={`Most ${config.audience} pour money into getting customers, then quietly lose them at renewal ${config.seasonalHook}.`}
        problems={config.problems}
      />

      <HowItWorksSection signupSearch={signupSearch} />
      <FeatureTabs tabs={nicheTabs} />
      <FeatureGrid
        eyebrow="Features"
        title={`Everything you need to keep ${config.planLabel.toLowerCase()}`}
        features={nicheFeatures}
      />

      <MetricsBand
        eyebrow="The math"
        title={`Know your number ${config.seasonalHook}`}
        body={metricsBody}
        metrics={nicheMetrics}
        signupSearch={signupSearch}
      />

      <PricingSection signupSearch={signupSearch} />
      {/* <BillingNoticeSection /> */}
      <NicheResources config={config} />
      <FaqSection faqs={config.faqs} />

      <FinalCta title="Find the revenue hiding in your member list" signupSearch={signupSearch} />
      <FooterSection />
      <MobileCta signupSearch={signupSearch} />
    </div>
  );
}

/* ══════════════════════════ Niche hero ══════════════════════════ */

function NicheHero({
  config,
  signupSearch,
  agreementRevenue,
}: {
  config: NicheConfig;
  signupSearch: { niche: NicheSlug };
  agreementRevenue: number;
}) {
  const rows = (NICHE_MOCKUP_ROWS[config.slug as NicheSlug] ?? NICHE_MOCKUP_ROWS.hvac).map((r) => ({
    name: r.name,
    reason: r.reason,
    risk: r.risk.split(' ')[0],
  }));
  const highRisk = rows.filter((r) => r.risk === 'High').length;
  const stats = [
    { label: 'Revenue at risk', value: `$${Math.round(agreementRevenue / 1000)}k`, icon: DollarSign, tone: 'text-danger' },
    { label: 'Likely to lapse', value: `${config.revenueExample.lapseRatePercent}%`, icon: TrendingDown, tone: 'text-warning' },
    { label: 'High risk', value: String(highRisk + 9), icon: AlertTriangle, tone: 'text-danger' },
    { label: 'Active plans', value: String(config.revenueExample.agreementCount), icon: FileText, tone: 'text-accent' },
  ];
  const expected = `$${(config.revenueExample.agreementCount * config.revenueExample.avgPlanPrice).toLocaleString()}`;

  return (
    <section className="mkt-grain relative overflow-hidden bg-ink pt-28 pb-16 md:pt-36 md:pb-24">
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(62% 55% at 50% -10%, rgba(91,132,255,0.28), transparent 62%), radial-gradient(40% 45% at 88% 8%, rgba(123,92,255,0.16), transparent 60%)',
        }}
      />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)',
          backgroundSize: '56px 56px',
          maskImage: 'radial-gradient(ellipse 78% 55% at 50% 0%, black, transparent 76%)',
        }}
      />

      <div className="relative mx-auto max-w-4xl px-4 text-center">
        <div className="mkt-reveal flex justify-center">
          <Eyebrow tone="dark">For {config.audience}</Eyebrow>
        </div>
        <h1 className="mkt-reveal mt-5 font-display text-[34px] font-semibold leading-[1.05] tracking-[-0.03em] text-ink-fg sm:text-[46px] md:text-[58px]">
          {config.heroTitle}
        </h1>
        <p
          className="mkt-reveal mx-auto mt-6 max-w-2xl text-[16px] leading-relaxed text-ink-muted sm:text-[18px]"
          style={{ animationDelay: '0.06s' }}
        >
          {config.heroSubhead}
        </p>

        <div
          className="mkt-reveal mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row"
          style={{ animationDelay: '0.12s' }}
        >
          <Cta to="/signup" search={signupSearch} size="lg" className="w-full sm:w-auto">
            Start free trial
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Cta>
          <Cta href="#how" variant="outline-dark" size="lg" className="w-full sm:w-auto">
            See how it works
          </Cta>
        </div>

        <div
          className="mkt-reveal mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[13px] text-ink-muted"
          style={{ animationDelay: '0.18s' }}
        >
          <span className="inline-flex items-center gap-1.5">
            <span className="flex">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className="h-3.5 w-3.5 fill-gold text-gold" />
              ))}
            </span>
            Loved by shop owners
          </span>
          <span className="hidden h-3 w-px bg-ink-line sm:block" />
          <span>No credit card required</span>
          <span className="hidden h-3 w-px bg-ink-line sm:block" />
          <span>Live in minutes</span>
        </div>
      </div>

      <div className="mkt-reveal relative mx-auto mt-16 max-w-5xl px-4" style={{ animationDelay: '0.24s' }}>
        <div
          className="mkt-glow pointer-events-none absolute -inset-x-16 -top-12 bottom-0"
          style={{ background: 'radial-gradient(50% 60% at 50% 0%, rgba(91,132,255,0.32), transparent 70%)' }}
        />
        <div className="relative">
          <FloatChip
            className="absolute -left-3 top-24 z-20 hidden animate-[mkt-float_6s_ease-in-out_infinite] lg:flex"
            icon={DollarSign}
            tone="accent"
            title={`$${Math.round(agreementRevenue / 1000)}k at risk`}
            sub="This season"
          />
          <FloatChip
            className="absolute -right-4 top-10 z-20 hidden animate-[mkt-float_7s_ease-in-out_infinite] lg:flex"
            style={{ animationDelay: '1s' }}
            icon={Bell}
            tone="indigo"
            title="Reminder sent"
            sub="Renewal sequence"
          />
          <MockDashboard
            title={`${config.companyName} — Renewal dashboard`}
            expected={expected}
            stats={stats}
            rows={rows}
          />
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════ Niche resources ══════════════════════════ */

function NicheResources({ config }: { config: NicheConfig }) {
  if (config.resources.length === 0) return null;
  return (
    <section className="border-t border-canvas-line bg-canvas-raised px-4 py-16 md:py-20">
      <div className="mx-auto max-w-5xl">
        <SectionHeading eyebrow="Resources" title={`Guides for ${config.audience}`} />
        <div className="mt-10 grid gap-4 sm:grid-cols-3">
          {config.resources.map((r) => (
            <Link
              key={r.slug}
              to="/blog/$slug"
              params={{ slug: r.slug }}
              className="group flex flex-col rounded-2xl border border-canvas-line bg-canvas p-5 transition-all duration-200 hover:border-accent/40 hover:shadow-[0_16px_40px_-22px_rgba(43,84,228,0.28)]"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent-soft text-accent transition-colors group-hover:bg-accent group-hover:text-white">
                <FileText className="h-5 w-5" />
              </span>
              <p className="mt-4 flex-1 text-[14.5px] font-medium leading-snug text-canvas-fg">{r.label}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-[13px] font-medium text-accent">
                Read guide
                <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
